import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { supabase } from "../lib/supabase";
import MarketingNav from "@/components/layout/MarketingNav";
import PartnerAuthBrandPanel from "@/components/partner/PartnerAuthBrandPanel";
import GoogleSignInButton from "@/components/partner/GoogleSignInButton";

const PartnerRegister = () => {
  const [institutionName, setInstitutionName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { signup, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!institutionName.trim()) {
      toast.error("Please enter your institution name!");
      return;
    }

    if (!email.trim() || !email.includes('@')) {
      toast.error("Please enter a valid email address!");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords don't match!");
      return;
    }

    if (password.length < 6) {
      toast.error("Password must be at least 6 characters long!");
      return;
    }

    setLoading(true);
    try {
      // First, try to sign up the user in Auth
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: institutionName,
            avatar_url: `https://i.pravatar.cc/150?u=${Date.now()}`
          },
        }
      });

      let userId: string;
      let session = authData.session;

      if (signUpError) {
        // Handle user already exists case
        if (signUpError.code === 'user_already_exists') {
          console.log("User already exists, attempting to sign in...");
          // Try to sign in instead
          const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
            email,
            password,
          });

          if (signInError) {
            console.error("Sign in error:", signInError);
            toast.error("Email already registered but password is incorrect. Please try logging in.");
            return;
          }

          if (signInData.user) {
            userId = signInData.user.id;
            session = signInData.session;
            console.log("Signed in successfully, user ID:", userId);
          } else {
            toast.error("Failed to sign in. Please try again.");
            return;
          }
        } else {
          // Log EVERYTHING about the error for debugging
          console.error("=== FULL SIGNUP ERROR ===");
          console.error("Error object:", signUpError);
          console.error("Error message:", signUpError.message);
          console.error("Error name:", signUpError.name);
          console.error("Error code:", signUpError.code);
          console.error("Error status:", signUpError.status);
          console.error("========================");
          
          // Extract the best possible error message
          let errorMessage = "An error occurred during signup.";
          
          // Handle specific error codes FIRST (more reliable than messages)
          if (signUpError.code === 'weak_password') {
            errorMessage = "Please choose a stronger password! Use at least 6 characters with a mix of letters and numbers.";
          } else if (signUpError.code === 'invalid_email') {
            errorMessage = "Please enter a valid email address!";
          } else if (signUpError.code === 'email_not_confirmed') {
            errorMessage = "Please verify your email address first!";
          } else {
            // Try to get message from various possible places
            if (signUpError.message) {
              errorMessage = signUpError.message;
            }
          }
          
          toast.error(errorMessage);
          return;
        }
      } else if (authData.user) {
        userId = authData.user.id;
        console.log("New user created, user ID:", userId);
      } else {
        toast.error("Failed to create user account.");
        return;
      }

      // RLS requires auth.uid() = id, so ensure we have an active session before any DB write.
      if (!session) {
        const { data: { session: existingSession } } = await supabase.auth.getSession();
        session = existingSession;
      }
      if (!session) {
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (signInError || !signInData.session) {
          toast.success(
            "Account created! Please verify your email, then sign in to finish partner setup."
          );
          navigate("/partners/login");
          return;
        }
        session = signInData.session;
      }

      // Check if partner record already exists
      const { data: existingPartner } = await supabase
        .from('partners')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (existingPartner) {
        console.log("Partner profile already exists:", existingPartner);
        toast.success("You already have a partner account!");
        navigate("/partners/dashboard");
        return;
      }

      // Create partner record (avoid .single() so a missing SELECT return can't hang/406)
      console.log("Attempting to create partner profile for user:", userId);

      const { error: partnerError, data: partnerRows } = await supabase
        .from('partners')
        .insert([
          {
            id: userId,
            institution_name: institutionName.trim(),
            email: email.trim().toLowerCase(),
          }
        ])
        .select('*');

      if (partnerError) {
        console.error("Error creating partner profile:", partnerError);
        console.error("Error details:", JSON.stringify(partnerError, null, 2));
        console.error("User ID:", userId);
        console.error("Institution name:", institutionName);
        console.error("Email:", email);

        // Unique email conflict: profile may already exist under this user
        if (partnerError.code === '23505') {
          const { data: retryPartner } = await supabase
            .from('partners')
            .select('*')
            .eq('id', userId)
            .maybeSingle();
          if (retryPartner) {
            toast.success("Partner account ready!");
            navigate("/partners/dashboard");
            return;
          }
        }

        toast.error(
          `Account created, but partner profile setup failed: ${partnerError.message}. Please sign in and try again, or contact support.`
        );
        navigate("/partners/login");
        return;
      }

      console.log("Partner profile created successfully:", partnerRows?.[0] ?? partnerRows);
      toast.success("Account created! Let's set up your organization profile.");
      navigate("/partners/organization");
    } catch (error: any) {
      console.error("Registration error:", error);
      toast.error(error.message || "An error occurred during registration.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = async () => {
    setGoogleLoading(true);
    try {
      await signInWithGoogle({ intent: "register" });
    } finally {
      setGoogleLoading(false);
    }
  };

  const busy = loading || googleLoading;

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-gray-950">
      <MarketingNav />

      <div className="flex-1 flex flex-col lg:flex-row">
        <PartnerAuthBrandPanel
          eyebrow="Partner portal"
          title="Join the network shaping the future of education."
          subtitle="Register your organization to reach high school students on Skutopia Discover."
        />

        <div className="flex-1 flex items-center justify-center p-6 sm:p-12 lg:p-16 bg-[#f8faf9] dark:bg-gray-950">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="w-full max-w-md"
        >
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">Create your partner account</h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Join organizations listing opportunities for high school students.
            </p>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200/80 dark:border-gray-800 p-6 sm:p-8 space-y-5">
            <GoogleSignInButton
              onClick={handleGoogleRegister}
              disabled={busy}
              label="Sign up with Google"
            />

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-200 dark:border-gray-700" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white dark:bg-gray-900 px-3 text-gray-400">
                  or register with email
                </span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="institutionName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Institution Name
                </label>
                <input
                  id="institutionName"
                  type="text"
                  value={institutionName}
                  onChange={(e) => setInstitutionName(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 dark:text-white focus:outline-none focus:ring-2 focus:ring-skutopia-500/30 focus:border-skutopia-500 text-sm"
                  placeholder="Your institution name"
                  required
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Work email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 dark:text-white focus:outline-none focus:ring-2 focus:ring-skutopia-500/30 focus:border-skutopia-500 text-sm"
                  placeholder="you@institution.com"
                  required
                  autoComplete="email"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 dark:text-white focus:outline-none focus:ring-2 focus:ring-skutopia-500/30 focus:border-skutopia-500 text-sm"
                  placeholder="Create a password"
                  required
                  autoComplete="new-password"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 dark:text-white focus:outline-none focus:ring-2 focus:ring-skutopia-500/30 focus:border-skutopia-500 text-sm"
                  placeholder="Confirm your password"
                  required
                  autoComplete="new-password"
                />
              </div>

              <Button
                type="submit"
                disabled={busy}
                className="w-full bg-skutopia-600 hover:bg-skutopia-700 text-white py-3 rounded-xl font-medium"
              >
                {loading ? "Creating Account..." : "Create Account"}
              </Button>
            </form>
          </div>

          <div className="mt-8 space-y-3 text-center text-sm">
            <p className="text-gray-500 dark:text-gray-400">
              Already have an account?{" "}
              <Link to="/partners/login" className="text-skutopia-600 hover:text-skutopia-700 font-medium">
                Sign in to your workspace
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
      </div>
    </div>
  );
};

export default PartnerRegister;
